import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useAuth } from 'react-oidc-context';
import axios from 'axios';
import { Storage } from '../../Utils/StorageUtils';
import { useAppDispatch } from '../Hooks';
import {
    login,
    logout,
    synchronizeUnauthenticatedState,
} from '../Reducers/AuthenticationReducer';
import {
    clearTokenGeneration,
    getActiveSessionGenerationId,
    resolveGenerationForToken,
} from '../Session/sessionInitialization';
import { AuthSessionStatus } from './authSessionTypes';

const AUTH_TOKEN_KEY = 'api-token-key';

type SessionInitializationAttempt = {
    token: string;
    generationId: number;
};

type AuthSessionSnapshot = {
    token: string | undefined;
    status: AuthSessionStatus;
    sessionGenerationId: number;
    isAuthenticated: boolean;
    user: ReturnType<typeof useAuth>['user'];
    error: unknown;
    retryInitialization: () => void;
    signOut: () => void;
};

type AuthSessionProviderProps = {
    children: React.ReactNode;
};

const AuthSessionContext = createContext<AuthSessionSnapshot | undefined>(undefined);

const applyAxiosAuthHeaders = (token: string, email?: string) => {
    axios.defaults.headers.common.Authorization = `Bearer ${token}`;
    if (email) {
        axios.defaults.headers.common.Email = email;
    }
};

export function AuthSessionProvider({ children }: AuthSessionProviderProps) {
    const auth = useAuth();
    const dispatch = useAppDispatch();
    const accessToken = auth.user?.access_token;
    const [status, setStatus] = useState<AuthSessionStatus>('resolving');
    const [initializationError, setInitializationError] = useState<unknown>(undefined);
    const [sessionGenerationId, setSessionGenerationId] = useState(0);
    const initializationGenerationRef = useRef(0);
    const inFlightAttemptRef = useRef<SessionInitializationAttempt | null>(null);
    const readyTokenRef = useRef<string | null>(null);

    const initializeSession = useCallback(
        async (generationId: number) => {
            if (!auth.isAuthenticated || !accessToken) {
                return;
            }

            const attempt = { token: accessToken, generationId };
            setStatus('initializing');
            setInitializationError(undefined);
            setSessionGenerationId(generationId);
            initializationGenerationRef.current = generationId;
            inFlightAttemptRef.current = attempt;

            applyAxiosAuthHeaders(accessToken, auth.user?.profile?.email);
            Storage.local.set(AUTH_TOKEN_KEY, accessToken);
            if (auth.user?.state) {
                Storage.local.set('auth_state', auth.user.state);
            }
            if (auth.user?.profile?.email) {
                Storage.local.set('user-mail', auth.user.profile.email);
            }

            try {
                await dispatch(login(auth, { generationId }) as never);
                if (
                    initializationGenerationRef.current === generationId &&
                    getActiveSessionGenerationId() === generationId
                ) {
                    readyTokenRef.current = accessToken;
                    setStatus('ready');
                }
            } catch (error) {
                if (
                    initializationGenerationRef.current === generationId &&
                    getActiveSessionGenerationId() === generationId
                ) {
                    setInitializationError(error);
                    setStatus('error');
                }
            } finally {
                if (inFlightAttemptRef.current === attempt) {
                    inFlightAttemptRef.current = null;
                }
            }
        },
        [accessToken, auth, dispatch]
    );

    const retryInitialization = useCallback(() => {
        if (!auth.isAuthenticated || !accessToken) {
            return;
        }
        readyTokenRef.current = null;
        clearTokenGeneration(accessToken);
        const generationId = resolveGenerationForToken(accessToken, true);
        void initializeSession(generationId);
    }, [accessToken, auth, initializeSession]);

    useEffect(() => {
        if (auth.isLoading) {
            setStatus('resolving');
            return;
        }

        if (auth.isAuthenticated && accessToken) {
            if (readyTokenRef.current === accessToken) {
                setStatus('ready');
                return;
            }
            if (inFlightAttemptRef.current?.token === accessToken) {
                return;
            }
            readyTokenRef.current = null;
            const generationId = resolveGenerationForToken(accessToken);
            void initializeSession(generationId);
            return;
        }

        clearTokenGeneration(accessToken ?? undefined);
        readyTokenRef.current = null;

        if (auth.error) {
            Storage.local.remove('auth_state');
            setSessionGenerationId(0);
            setStatus('error');
            setInitializationError(auth.error);
            return;
        }

        Storage.local.remove(AUTH_TOKEN_KEY);
        Storage.local.remove('auth_state');
        setSessionGenerationId(0);
        setStatus('unauthenticated');
        dispatch(synchronizeUnauthenticatedState());
    }, [
        accessToken,
        auth.error,
        auth.isAuthenticated,
        auth.isLoading,
        dispatch,
        initializeSession,
    ]);

    const signOut = useCallback(() => {
        readyTokenRef.current = null;
        inFlightAttemptRef.current = null;
        initializationGenerationRef.current += 1;
        clearTokenGeneration(accessToken ?? undefined);
        setSessionGenerationId(0);
        setInitializationError(undefined);
        setStatus('unauthenticated');
        Storage.local.remove('auth_state');
        dispatch(logout());
        void auth.signoutRedirect();
    }, [accessToken, auth, dispatch]);

    const snapshot = useMemo<AuthSessionSnapshot>(
        () => ({
            token: accessToken,
            status,
            sessionGenerationId,
            isAuthenticated: status === 'ready',
            user: auth.user,
            error: initializationError ?? auth.error,
            retryInitialization,
            signOut,
        }),
        [
            accessToken,
            auth.error,
            auth.user,
            initializationError,
            retryInitialization,
            sessionGenerationId,
            signOut,
            status,
        ]
    );

    return <AuthSessionContext.Provider value={snapshot}>{children}</AuthSessionContext.Provider>;
}

export function useAuthToken(): AuthSessionSnapshot {
    const snapshot = useContext(AuthSessionContext);
    if (!snapshot) {
        throw new Error('useAuthToken must be used within an AuthSessionProvider');
    }
    return snapshot;
}
